import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { UserPlus, LogOut, X } from 'lucide-react';
import usePopoverPosition from '../hooks/usePopoverPosition';
import UserAvatar from './ui/UserAvatar';
import AvatarStatusBadge from './ui/AvatarStatusBadge';
import chatService from '../services/chatService';
import { useChatStore } from '../store/chatStore';
import { useNotificationStore } from '../store/notificationStore';
import { forgetCachedChannel } from '../services/chatHistoryCache';
import { isCompleteEmail } from './chatHelpers';
import './ChannelMembersPopover.css';

/**
 * Channel members popover, anchored to the member-count button in the chat
 * header. Same controlled open/anchorRef/onClose portal recipe as
 * ForwardChannelPicker (ChatMessageView). Lists members with presence and
 * roles; owners/admins can add people by exact email and remove members;
 * non-owners can leave. DMs are read-only (just the two members).
 */
export default function ChannelMembersPopover({
  open,
  anchorRef,
  channel,
  currentUserId,
  online,
  onClose,
}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirmRemoveId, setConfirmRemoveId] = useState(null);
  const [confirmLeave, setConfirmLeave] = useState(false);
  const searchRef = useRef(null);
  const debounceRef = useRef(null);

  const bounds = useCallback(
    () => anchorRef.current?.closest('.chat-view')?.getBoundingClientRect() ?? null,
    [anchorRef]
  );
  const pos = usePopoverPosition({ anchorRef, open, width: 280, align: 'end', bounds });

  const members = channel?.members || [];
  const isSelf = (id) => String(id) === String(currentUserId);
  const myRole = members.find((m) => isSelf(m.user_id))?.role;
  const isOwner = myRole === 'owner' || String(channel?.owner_id) === String(currentUserId);
  const canManage = channel?.kind !== 'dm' && (isOwner || myRole === 'admin');
  const canLeave = channel?.kind !== 'dm' && !isOwner;

  const emailMode = isCompleteEmail(query);

  useEffect(() => {
    if (!open) {
      setQuery('');
      setResults([]);
      setSearching(false);
      setConfirmRemoveId(null);
      setConfirmLeave(false);
      return undefined;
    }
    searchRef.current?.focus();
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // People lookup — exact-email only, debounced 250ms (mirrors ChatTitleBarSearch).
  useEffect(() => {
    clearTimeout(debounceRef.current);
    if (!open || !emailMode) {
      setResults([]);
      setSearching(false);
      return undefined;
    }
    let cancelled = false;
    setResults([]);
    setSearching(true);
    debounceRef.current = setTimeout(() => {
      chatService
        .searchUsers(query.trim())
        .then((users) => {
          if (cancelled) return;
          setResults(Array.isArray(users) ? users : []);
          setSearching(false);
        })
        .catch(() => {
          if (cancelled) return;
          setResults([]);
          setSearching(false);
        });
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(debounceRef.current);
    };
  }, [open, query, emailMode]);

  if (!open || !channel || typeof document === 'undefined' || !document.body) return null;

  const isOnline = (userId) =>
    (online || []).some((o) => String(o.user_id ?? o.id) === String(userId));

  const sorted = [...members].sort((a, b) => {
    const diff = (isOnline(a.user_id) ? 0 : 1) - (isOnline(b.user_id) ? 0 : 1);
    if (diff !== 0) return diff;
    return (a.user?.name || '').localeCompare(b.user?.name || '');
  });

  const addUser = async (user) => {
    if (busy) return;
    setBusy(true);
    try {
      const updated = await chatService.addMember(channel.id, user.id);
      useChatStore.getState().upsertChannel(updated);
      setQuery('');
      setResults([]);
    } catch (e) {
      useNotificationStore.getState().error(e.response?.data?.error || 'Could not add member');
    } finally {
      setBusy(false);
    }
  };

  const removeUser = async (member) => {
    if (busy) return;
    setBusy(true);
    try {
      await chatService.removeMember(channel.id, member.user_id);
      useChatStore.getState().upsertChannel({
        ...channel,
        members: members.filter((m) => String(m.user_id) !== String(member.user_id)),
      });
      setConfirmRemoveId(null);
    } catch (e) {
      useNotificationStore.getState().error(e.response?.data?.error || 'Could not remove member');
    } finally {
      setBusy(false);
    }
  };

  const leave = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await chatService.removeMember(channel.id, currentUserId);
      // Drops the conversation plus all its IN-MEMORY per-channel state
      // (buffer, unread, presence, keys) and clears the active selection.
      useChatStore.getState().removeChannel(channel.id);
      // The on-disk history is separate state and removeChannel does not touch
      // it. `useChatRealtime` also purges it from `notify_channel_removed`,
      // which the server does broadcast to the leaver — but that is a SOCKET
      // delivery for a REST call that has already succeeded, so if the socket
      // is down at this moment nothing ever retries and the conversation's
      // history keeps rendering from cache on the next launch. Doing it here
      // makes the leave self-contained; both paths are idempotent.
      forgetCachedChannel(channel.id);
      onClose?.();
    } catch (e) {
      useNotificationStore.getState().error(e.response?.data?.error || 'Could not leave channel');
    } finally {
      setBusy(false);
    }
  };

  return createPortal(
    <>
      <div
        className="emoji-reaction-backdrop"
        aria-hidden
        onMouseDown={(e) => {
          e.preventDefault();
          onClose?.();
        }}
      />
      <div
        className="chat-members-popover"
        style={{
          position: 'fixed',
          ...(pos.dir === 'up' ? { bottom: pos.bottom } : { top: pos.top }),
          left: pos.left,
          width: pos.width,
          maxHeight: pos.maxHeight,
          zIndex: 10040,
        }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="chat-members-header">Members — {members.length}</div>

        {canManage && (
          <div className="chat-members-add">
            <div className="chat-members-add-input">
              <UserPlus size={12} className="chat-members-add-icon" aria-hidden />
              <input
                ref={searchRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Add by email…"
                aria-label="Add member by email"
              />
            </div>
            {emailMode && (
              <div className="chat-members-add-results">
                {results.map((u) => {
                  const already = members.some((m) => String(m.user_id) === String(u.id));
                  return (
                    <button
                      key={u.id}
                      type="button"
                      className="chat-members-result"
                      disabled={already || busy}
                      onClick={() => addUser(u)}
                    >
                      <UserAvatar userId={u.id} name={u.name} size={26} />
                      <span className="chat-members-result-text">
                        <span className="chat-members-result-name">{u.name}</span>
                        <span className="chat-members-result-email">
                          {already ? 'Already a member' : u.email}
                        </span>
                      </span>
                    </button>
                  );
                })}
                {results.length === 0 && (
                  <div className="chat-members-hint">
                    {searching ? 'Searching…' : 'No user found with that email address.'}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        <div className="chat-members-list">
          {sorted.map((m) => {
            const self = isSelf(m.user_id);
            const ownerRow = m.role === 'owner' || String(m.user_id) === String(channel.owner_id);
            const removable = canManage && !ownerRow && !self;
            return (
              <div key={m.user_id} className="chat-members-row">
                <UserAvatar userId={m.user_id} name={m.user?.name} size={26}>
                  <AvatarStatusBadge userId={m.user_id} avatarSize={26} />
                </UserAvatar>
                <span className="chat-members-name">
                  {m.user?.name || 'Unknown'}
                  {self && <span className="chat-members-you"> (you)</span>}
                </span>
                {(m.role === 'owner' || m.role === 'admin') && (
                  <span className={`chat-members-role chat-members-role--${m.role}`}>{m.role}</span>
                )}
                {removable &&
                  (confirmRemoveId === m.user_id ? (
                    <span className="chat-members-confirm">
                      <button
                        type="button"
                        className="chat-members-action chat-members-action--danger"
                        disabled={busy}
                        onClick={() => removeUser(m)}
                      >
                        Remove
                      </button>
                      <button
                        type="button"
                        className="chat-members-action"
                        disabled={busy}
                        onClick={() => setConfirmRemoveId(null)}
                      >
                        Cancel
                      </button>
                    </span>
                  ) : (
                    <button
                      type="button"
                      className="chat-members-remove"
                      title="Remove member"
                      onClick={() => setConfirmRemoveId(m.user_id)}
                    >
                      <X size={12} />
                    </button>
                  ))}
              </div>
            );
          })}
        </div>

        {canLeave && (
          <div className="chat-members-footer">
            {confirmLeave ? (
              <>
                <span className="chat-members-footer-label">Leave this channel?</span>
                <button
                  type="button"
                  className="chat-members-action chat-members-action--danger"
                  disabled={busy}
                  onClick={leave}
                >
                  Leave
                </button>
                <button
                  type="button"
                  className="chat-members-action"
                  disabled={busy}
                  onClick={() => setConfirmLeave(false)}
                >
                  Cancel
                </button>
              </>
            ) : (
              <button
                type="button"
                className="chat-members-leave"
                onClick={() => setConfirmLeave(true)}
              >
                <LogOut size={12} aria-hidden />
                Leave channel
              </button>
            )}
          </div>
        )}
      </div>
    </>,
    document.body
  );
}
